// ==================== FILE: src/pages/CampaignFlowBuilder.jsx ====================
// This is the MAIN PAGE component that contains the complete flow builder

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Flex, Text, useToast, Box, IconButton, Tooltip } from '@chakra-ui/react';
import { FiChevronLeft } from 'react-icons/fi';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactFlow, {
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    MarkerType,
    BackgroundVariant,
    Background,
    Controls,
} from 'reactflow';
import 'reactflow/dist/style.css';
// after reactflow's own stylesheet so the uniform handle sizing wins
import './flowHandles.css';

import FlowHeader from './FlowHeader';
import FlowToolbar from './FlowToolbar';
import ComponentLibrary from './ComponentLibrary';
import PropertyPanel from './PropertyPanel';
import TemplatesDialog from './TemplatesDialog';
import { nodeTypes } from './nodes';
import { loadTemplateFlows } from './utils/flowTemplates';
import { EDGE_DEFAULTS, edgeStyle, withEdgeStyles } from './utils/edgeStyles';

import API from '../../helpers/axiosInstance';

import { transformToBackendPayload } from './utils/Payload';
import { getApiHeaders } from '../../utils/DefaultFunction';
import { transformBackendToReactFlow } from './NodeHelper';


export default function CampaignFlowBuilder() {
    const navigate = useNavigate();
    const { id } = useParams(); // Get flow ID from URL if editing
    const location = useLocation();
    const toast = useToast();

    // Determine if we're in edit mode
    const isEdit = Boolean(id);
    const flowDataFromRoute = location.state?.flowData || null;
    const [lastNodePosition, setLastNodePosition] = useState({ x: 250, y: 200 });

    // ==================== STATE MANAGEMENT ====================
    const [nodes, setNodes] = useState(flowDataFromRoute?.nodes || []);
    // Restyled on the way in as well: a flow handed over through router state was built
    // by whichever screen navigated here, so its edge colours are not guaranteed.
    const [edges, setEdges] = useState(() =>
        withEdgeStyles(flowDataFromRoute?.edges, flowDataFromRoute?.nodes)
    );
    const [selectedNode, setSelectedNode] = useState(null);
    const [campaignName, setCampaignName] = useState(flowDataFromRoute?.name || 'New Campaign Flow');
    const [flowStatus, setFlowStatus] = useState(flowDataFromRoute?.status === 'active' ? 'active' : 'draft');
    const [showGrid, setShowGrid] = useState(true);
    const [examplesDialogOpen, setExamplesDialogOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(isEdit && !flowDataFromRoute);
    const [isLeftPanelMinimized, setIsLeftPanelMinimized] = useState(false);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

    const nodesRef = useRef([]);
    const selectedNodeRef = useRef(null);
    // Tracks the flow ID after first create so Save & Continue reuses the same record
    const savedFlowIdRef = useRef(id || null);

    // Inside the CampaignFlowBuilder component, add this state
    const [currentZoom, setCurrentZoom] = useState(100);

    // Add a ref for the ReactFlow instance
    const reactFlowInstance = useRef(null);

    // Add these handler functions
    const handleZoomIn = useCallback(() => {
        if (reactFlowInstance.current) {
            reactFlowInstance.current.zoomIn({ duration: 200 });
            setTimeout(() => {
                const zoom = reactFlowInstance.current.getZoom();
                setCurrentZoom(Math.round(zoom * 100));
            }, 250);
        }
    }, []);

    const handleZoomOut = useCallback(() => {
        if (reactFlowInstance.current) {
            reactFlowInstance.current.zoomOut({ duration: 200 });
            setTimeout(() => {
                const zoom = reactFlowInstance.current.getZoom();
                setCurrentZoom(Math.round(zoom * 100));
            }, 250);
        }
    }, []);

    const handleFitView = useCallback(() => {
        if (reactFlowInstance.current) {
            reactFlowInstance.current.fitView({
                padding: 0.2,
                duration: 400,
                maxZoom: 1.5,
                minZoom: 0.5,
            });
            setTimeout(() => {
                const zoom = reactFlowInstance.current.getZoom();
                setCurrentZoom(Math.round(zoom * 100));
            }, 450);
        }
    }, []);


    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Delete' || event.key === 'Backspace') {
                setEdges((eds) => eds.filter((edge) => !edge.selected));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
    useEffect(() => {
        selectedNodeRef.current = selectedNode;
    }, [selectedNode]);

    // Load flow data if editing and not passed via route
    useEffect(() => {
        if (isEdit && !flowDataFromRoute) {
            loadFlowData(id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit, flowDataFromRoute]);

    // Node configuration state
    const [nodeConfig, setNodeConfig] = useState({
        label: '',
        channel: '',
        template: '',
        templateId: '',
        duration: '',

        unit: 'days',
        eventName: '',
        eventSource: '',
        conditionLogic: 'occurred',
        timeWindowValue: '1',
        timeWindowUnit: 'days',
        yesLabel: 'Yes',
        noLabel: 'No',
        fallbackEnabled: false,
        triggerType: 'event',
        eventTriggerMode: 'new_event',
        eventConditions: '',
        eventProperties: [
            {
                attribute: '',
                operator: 'equals',
                value: ''
            }
        ]
        ,
        apiEndpoint: '',
        apiMethod: 'GET',
        apiKey: '',
        apiPayload: '',
        apiHeaders: '',
        enableAttributeMatching: false,
        matchAttribute: '',
        referenceType: 'static',
        previousEventRef: '',
        messageVariable: '',
        staticValue: '',
        comparisonType: 'equals',
        attributeFallbackEnabled: false,

        numBranches: 2,
        branches: { A: 50, B: 50 },

        waitTime: 24,
        waitUnit: 'hours',
        trackingMode: 'read',
        tagName: '',
        customerSegmentId: '',
        segmentName: '',
        segmentEntryMode: 'existing', // existing | new
        segmentExitOnMatch: false,
        customerCount: 0,
        allCoustomer: false,
        variableMappings: {},
        messageType: 'template',
        freeformMessage: '',
        delaySubtype: "",

        enableSchedule: false,
        scheduleType: 'daily',
        scheduleTime: '09:00',
        scheduleTimezone: 'Asia/Kolkata',
        scheduleDayOfWeek: 'monday',
        scheduleDayOfMonth: '1',

        // tagValue: '',
    });



    const loadFlowData = async (flowId) => {
        setIsLoading(true);
        try {
            const response = await API.get(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/by-id?id=${flowId}`,
                { headers: getApiHeaders() }
            );

            if (response.data) {
                const flowData = response.data;

                // Transform backend data to React Flow format
                const { nodes: transformedNodes, edges: transformedEdges } = transformBackendToReactFlow(flowData, handleDelete, handleDuplicate, handleEdit);

                setNodes(transformedNodes);
                setEdges(transformedEdges);
                setCampaignName(flowData.name || 'Campaign Flow');
                setFlowStatus(flowData.status || 'draft');


            }
        } catch (error) {
            console.error('Error loading flow:', error);
            toast({
                title: 'Error',
                description: 'Failed to load flow data',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            navigate('/marketing/flow');
        } finally {
            setIsLoading(false);
        }
    };

    const createFlow = async (payload) => {
        try {
            const response = await API.post(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/create`,
                payload,
                { headers: getApiHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Error creating flow:', error);
            throw error;
        }
    };

    const updateFlow = async (flowId, payload) => {
        try {
            const response = await API.post(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/update?id=${flowId}`,
                payload,
                { headers: getApiHeaders() }
            );
            return response.data;
        } catch (error) {
            console.error('Error updating flow:', error);
            throw error;
        }
    };

    // ==================== EVENT HANDLERS ====================

    // Update the handleEdit function (around line 548) - add these cases to configMap:
    const handleEdit = useCallback((nodeId) => {
        const node = nodesRef.current.find((n) => n.id === nodeId);
        if (!node) return;
        setSelectedNode(node);
        setIsRightPanelOpen(true);



        const configMap = {

            dtmfNode: () => ({
                label: node.data.label || '',
                dtmfResponses: node.data.dtmfResponses || [],
                noResponseWaitEnabled: node.data.noResponseWaitEnabled || false,
                noResponseWaitTime: node.data.noResponseWaitTime || 30,
                noResponseWaitUnit: node.data.noResponseWaitUnit || 'seconds',
            }),

            smsInboundNode: () => ({
                label: node.data.label || '',
                smsResponses: node.data.smsResponses || [],
                noResponseWaitEnabled: node.data.noResponseWaitEnabled || false,
                noResponseWaitTime: node.data.noResponseWaitTime || 24,
                noResponseWaitUnit: node.data.noResponseWaitUnit || 'hours',
            }),

            smartConditionNode: () => ({
                label: node.data.label || '',
                groups: node.data.groups || [],
                defaultLabel: node.data.defaultLabel || 'No Match',
            }),

            customerProfileNode: () => {
                // Normalize: support both multi-condition array and legacy single fields
                const conditions =
                    node.data.conditions && node.data.conditions.length > 0
                        ? node.data.conditions
                        : node.data.profileKey
                            ? [{
                                profile_key: node.data.profileKey,
                                operator: node.data.operator || 'equals',
                                match_value: node.data.matchValue || '',
                            }]
                            : [];
                return {
                    label: node.data.label || '',
                    conditions: conditions,
                    trueLabel: node.data.trueLabel || 'Match Found (True)',
                    falseLabel: node.data.falseLabel || 'No Match (False)',
                };
            },
            checkChannelConsentNode: () => ({
                label: node.data.label || '',
                channel: node.data.channel || '',
                channelLabel: node.data.channelLabel || '',
                enabledLabel: node.data.enabledLabel || 'Enabled',
                disabledLabel: node.data.disabledLabel || 'Disabled',
            }),

            updateChannelConsentNode: () => ({
                label: node.data.label || '',
                channel: node.data.channel || '',
                channelLabel: node.data.channelLabel || '',
                consentAction: node.data.consentAction || '',
            }),

            enhancedConditionNode: () => ({
                label: node.data.label || '',
                conditionType: node.data.conditionType || 'conversion',
                conversionDefinition: node.data.conversionDefinition || '',
                matchEntityBy: node.data.matchEntityBy || 'order_id',
                journeyState: node.data.journeyState || '',
                attributeSource: node.data.attributeSource || 'profile',
                fieldSelector: node.data.fieldSelector || '',
                eventName: node.data.eventName || '',
                countOperator: node.data.countOperator || '>',
                countValue: node.data.countValue || '0',
                yesLabel: node.data.yesLabel || 'Yes',
                noLabel: node.data.noLabel || 'No',
            }),

            ivrNode: () => ({
                label: node.data.label || '',
                templateId: node.data.templateId || null,
                templateName: node.data.templateName || '',
                voiceScript: node.data.voiceScript || '',
                ivrVariableMappings: node.data.ivrVariableMappings || {},
                enableRetries: node.data.enableRetries || false,
                noOfRetries: node.data.noOfRetries || 3,
                waitTimeBetweenRetries: node.data.waitTimeBetweenRetries || 5,
                waitTimeUnit: node.data.waitTimeUnit || 'minutes',
            }),
            actionNode: () => ({
                label: node.data.label || '',
                channel: node.data.channel || '',
                template: node.data.template || '',
                templateId: node.data.templateId || '',
                variableMappings: node.data.variableMappings || {},
                messageType: node.data.messageType || 'template', // Add this
                freeformMessage: node.data.freeformMessage || '', // Add this
            }),
            delayNode: () => ({
                label: node.data.label || '',
                duration: node.data.duration || '',
                unit: node.data.unit || 'days',
            }),
            conditionNode: () => ({
                label: node.data.label || '',
                eventSource: node.data.eventSource || '',
                conditionLogic: node.data.conditionLogic || 'occurred',
                yesLabel: node.data.yesLabel || 'Yes',
                noLabel: node.data.noLabel || 'No',
                fallbackEnabled: node.data.fallbackEnabled || false,

                // Time window fields (only used when conditionLogic is 'within_time')
                timeWindowValue: node.data.timeWindowValue || '1',
                timeWindowUnit: node.data.timeWindowUnit || 'days',

                // Attribute check fields (only used when conditionLogic is 'attribute_check')
                attributeName: node.data.attributeName || '',
                operator: node.data.operator || 'equals',
                attributeValue: node.data.attributeValue || '',

                // Count fields (only used when conditionLogic is 'count_of_events')
                countOperator: node.data.countOperator || 'equals',
                countValue: node.data.countValue || '',

                // Attribute matching (advanced)
                enableAttributeMatching: node.data.enableAttributeMatching || false,
                matchAttribute: node.data.matchAttribute || '',
                referenceType: node.data.referenceType || 'static',
                previousEventRef: node.data.previousEventRef || '',
                messageVariable: node.data.messageVariable || '',
                staticValue: node.data.staticValue || '',
                comparisonType: node.data.comparisonType || 'equals',
                attributeFallbackEnabled: node.data.attributeFallbackEnabled || false,
            }),
            startNode: () => ({
                triggerType: node.data.triggerType || 'event',
                eventTriggerMode: node.data.eventTriggerMode || 'new_event',
                eventName: node.data.eventName || '',
                eventConditions: node.data.eventConditions || '',
                eventProperties: node.data.eventProperties || [],
                customerSegmentId: node.data.customerSegmentId || '',
                segmentEntryMode: node.data.segmentEntryMode || 'existing',
                segmentExitOnMatch: node.data.segmentExitOnMatch || false,
                customerCount: node.data.customerCount || 0,
                allCustomers: node.data.allCustomers || false,
                segmentName: node.data.segmentName || '',
                enableSchedule: node.data.enableSchedule || false,
                scheduleType: node.data.scheduleType || 'daily',
                scheduleTime: node.data.scheduleTime || '09:00',
                scheduleTimezone: node.data.scheduleTimezone || 'Asia/Kolkata',
                scheduleDayOfWeek: node.data.scheduleDayOfWeek || 'monday',
                scheduleDayOfMonth: node.data.scheduleDayOfMonth || '1',
                triggerContractFields: node.data.triggerContractFields || [],
                triggerSampleJson: node.data.triggerSampleJson || '',
            }),
            apiNode: () => ({
                label: node.data.label || '',
                apiEndpoint: node.data.apiEndpoint || node.data.endpoint || '',
                apiMethod: node.data.apiMethod || node.data.method || 'GET',
                apiKey: node.data.apiKey || 'apikey',
                authType: node.data.authType || 'none',
                authHeaderKey: node.data.authHeaderKey || 'X-API-Key',
                bearerToken: node.data.bearerToken || '',
                basicUsername: node.data.basicUsername || '',
                basicPassword: node.data.basicPassword || '',
                executionMode: node.data.executionMode || 'sync',
                timeout: node.data.timeout || 30,
                responseKey: node.data.responseKey || 'api_response',
                queryParams: node.data.queryParams || [],
                headers: node.data.headers || [{ key: '', value: '' }],
                bodyType: node.data.bodyType || 'json',
                apiPayload: node.data.apiPayload || node.data.payload || '',
                responseMapping: node.data.responseMapping || [{ responseField: '', saveAs: '' }],
                handle4xx: node.data.handle4xx || false,
                handle5xx: node.data.handle5xx || false,
                retryEnabled: node.data.retryEnabled || false,
                retryAttempts: node.data.retryAttempts || 3,
                retryStrategy: node.data.retryStrategy || 'exponential',
                responseNamespace: node.data.responseNamespace || 'api_response',
                contractFields: node.data.contractFields || [],
                sampleJson: node.data.sampleJson || '',
            }),
            // NEW: A/B Test Node
            abTestNode: () => ({
                label: node.data.label || '',
                numBranches: node.data.numBranches || 2,
                branches: node.data.branches || { A: 50, B: 50 },
            }),
            // NEW: Message Read Node
            messageReadNode: () => ({
                label: node.data.label || '',
                waitTime: node.data.waitTime || 24,
                waitUnit: node.data.waitUnit || 'hours',
                trackingMode: node.data.trackingMode || 'read',
            }),
            // NEW: Update Profile Node
            updateProfileNode: () => ({
                label: node.data.label || '',
                tagName: node.data.tagName || '',
                // tagValue: node.data.tagValue || '',
            }),
            dynamicDelayNode: () => ({
                label: node.data.label || '',
                delaySubtype: node.data.type,
                type: node.data.type,
                dynamicWaitSource: node.data.dynamicWaitSource || 'event',
                dynamicEventFieldType: node.data.dynamicEventFieldType || 'resume_at',
                dynamicEventFieldPath: node.data.dynamicEventFieldPath || 'event.delivery.resume_at',
                dynamicApiNode: node.data.dynamicApiNode || '',
                dynamicApiField: node.data.dynamicApiField || 'resume_at',
                dynamicExpression: node.data.dynamicExpression || '',
                fallbackDuration: node.data.fallbackDuration || '1',
                fallbackUnit: node.data.fallbackUnit || 'days',
                maxWaitDuration: node.data.maxWaitDuration || '30',
                maxWaitUnit: node.data.maxWaitUnit || 'days',
                duration: node.data.duration,
                unit: node.data.unit,
            }),

            callFlowNode: () => ({
                label: node.data.label || '',
                selectedFlowId: node.data.selectedFlowId || '',
                selectedFlowName: node.data.selectedFlowName || '',
                flowStatus: node.data.flowStatus || '',
            }),

            callWhatsappFlowNode: () => ({
                label: node.data.label || '',
                flowId: node.data.flowId || '',
                bodyText: node.data.bodyText || '',
                kvPairs: node.data.kvPairs || [],
                timeoutEnabled: node.data.timeoutEnabled || false,
                timeoutWaitTime: node.data.timeoutWaitTime || 30,
                timeoutWaitUnit: node.data.timeoutWaitUnit || 'minutes',
                timeoutWaitReference: node.data.timeoutWaitReference || 'flow_trigger',
            }),

            multiStepNode: () => ({
                label: node.data.label || '',
                stepCount: node.data.stepCount || 3,
                stepNames: node.data.stepNames || Array.from({ length: node.data.stepCount || 3 }, (_, i) => `Step ${i + 1}`),
            }),

            mergeNode: () => ({
                label: node.data.label || '',
                inputCount: node.data.inputCount || 3,
                mergeBehavior: node.data.mergeBehavior || 'any',
                continueOnce: node.data.continueOnce !== false,
                timeoutEnabled: node.data.timeoutEnabled || false,
                timeoutValue: node.data.timeoutValue || 30,
                timeoutUnit: node.data.timeoutUnit || 'minutes',
                continueOnTimeout: node.data.continueOnTimeout || false,
            }),

            buttonClickedNode: () => {
                const buttons = node.data.buttons || [];

                // Ensure we have exactly 3 button slots
                const normalizedButtons = [0, 1, 2].map(index => {
                    const button = buttons[index];
                    if (button && button.name) {
                        return {
                            name: button.name,
                            id: button.id || `button_${index + 1}`,
                            conditionEnabled: button.conditionEnabled || false,
                            conditionTime: button.conditionTime || 60,
                            conditionUnit: button.conditionUnit || 'minutes',
                            conditionReference: button.conditionReference || 'message_sent',
                        };
                    }
                    return {
                        name: '',
                        id: `button_${index + 1}`,
                        conditionEnabled: false,
                        conditionTime: 60,
                        conditionUnit: 'minutes',
                        conditionReference: 'message_sent',
                    };
                });

                return {
                    label: node.data.label || '',
                    buttons: normalizedButtons,
                    noClickWaitEnabled: node.data.noClickWaitEnabled || false,
                    noClickWaitTime: node.data.noClickWaitTime || 60,
                    noClickWaitUnit: node.data.noClickWaitUnit || 'minutes',
                    noClickWaitReference: node.data.noClickWaitReference || 'message_sent',
                };
            },
        };

        const config = configMap[node.type]?.() || {};
        setNodeConfig(config);
    }, []);

    const handleDelete = useCallback((nodeId) => {
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
        if (selectedNodeRef.current?.id === nodeId) {
            setSelectedNode(null);
        }
        toast({
            title: 'Node deleted',
            status: 'info',
            duration: 2000,
            isClosable: true,
        });
    }, [toast]);

    const handleDuplicate = useCallback((nodeId) => {
        setNodes((nds) => {
            const node = nds.find((n) => n.id === nodeId);
            if (!node) return nds;

            const newId = `${node.type}-${Date.now()}`;

            // Generate a unique label by appending (1), (2), etc.
            const baseLabel = node.data?.label?.trim() || '';
            const existingLabels = new Set(nds.map(n => n.data?.label?.trim()).filter(Boolean));
            let uniqueLabel = baseLabel;
            if (baseLabel) {
                let counter = 1;
                while (existingLabels.has(uniqueLabel)) {
                    uniqueLabel = `${baseLabel} (${counter})`;
                    counter++;
                }
            }

            const newNode = {
                ...node,
                id: newId,
                position: { x: node.position.x + 50, y: node.position.y + 50 },
                data: { ...node.data, id: newId, label: uniqueLabel },
                // The copy is the node the user is now working with, so it takes the
                // selection. Spreading the source would otherwise inherit its
                // `selected` / `dragging` flags verbatim.
                selected: true,
                dragging: false,
            };

            // Drop the selection from everything else — the source node included.
            // React Flow drags every selected node together, so leaving the original
            // selected made the copy and the original move as a pair until the user
            // clicked the canvas to clear it.
            return [
                ...nds.map((n) => (n.selected ? { ...n, selected: false } : n)),
                newNode,
            ];
        });
        toast({
            title: 'Node duplicated',
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    }, [toast]);

    // Update the addNode function (around line 633) - add these cases to nodeDefaults:
    const addNode = useCallback((type, subtype) => {
        const newId = `${type}-${Date.now()}`;

        const viewportCenter = reactFlowInstance.current?.getViewport();
        let position = { x: 250, y: 200 };
        if (reactFlowInstance.current) {
            const viewport = reactFlowInstance.current.getViewport();
            const bounds = { width: window.innerWidth, height: window.innerHeight };

            // Calculate center position in flow coordinates
            const centerX = (bounds.width / 2 - viewport.x) / viewport.zoom;
            const centerY = (bounds.height / 2 - viewport.y) / viewport.zoom;

            position = { x: centerX, y: centerY };
        }
        setLastNodePosition(position);



        const nodeDefaults = {
            start: {
                type: 'startNode',
                data: {
                    id: newId,
                    label: '',
                    triggerType: 'event',
                    eventProperties: [
                        {
                            attribute: '',
                            operator: 'equals',
                            value: ''
                        }
                    ],
                    eventTriggerMode: 'new_event',
                    eventName: '',
                    customerSegmentId: '',
                    segmentEntryMode: 'existing',
                    segmentExitOnMatch: false,
                    customerCount: 0,
                    segmentName: '',
                    allCustomers: false,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                },
            },

            dtmfNode: {
                type: 'dtmfNode',
                data: {
                    id: newId,
                    label: 'DTMF Response',
                    dtmfResponses: [],
                    noResponseWaitEnabled: true,
                    noResponseWaitTime: 30,
                    noResponseWaitUnit: 'seconds',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            smsInboundNode: {
                type: 'smsInboundNode',
                data: {
                    id: newId,
                    label: 'SMS Inbound',
                    smsResponses: [],
                    noResponseWaitEnabled: true,
                    noResponseWaitTime: 24,
                    noResponseWaitUnit: 'hours',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            smartConditionNode: {
                type: 'smartConditionNode',
                data: {
                    id: newId,
                    label: 'Smart Condition',
                    groups: [],
                    defaultLabel: 'No Match',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            customerProfile: {
                type: 'customerProfileNode',
                data: {
                    id: newId,
                    label: 'Check Customer Profile',
                    conditions: [],
                    trueLabel: 'Match Found (True)',
                    falseLabel: 'No Match (False)',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            checkChannelConsent: {
                type: 'checkChannelConsentNode',
                data: {
                    id: newId,
                    label: 'Check Channel Consent',
                    channel: '',
                    channelLabel: '',
                    enabledLabel: 'Enabled',
                    disabledLabel: 'Disabled',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            updateChannelConsent: {
                type: 'updateChannelConsentNode',
                data: {
                    id: newId,
                    label: 'Update Channel Consent',
                    channel: '',
                    channelLabel: '',
                    consentAction: '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            enhancedConditionNode: {
                type: 'enhancedConditionNode',
                data: {
                    id: newId,
                    label: 'Enhanced Condition',
                    conditionType: 'conversion',
                    conversionDefinition: '',
                    matchEntityBy: 'order_id',
                    yesLabel: 'Yes',
                    noLabel: 'No',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },

            ivrNode: {
                type: 'ivrNode',
                data: {
                    id: newId,
                    label: 'IVR Call',
                    templateId: null,
                    templateName: '',
                    voiceScript: '',
                    ivrVariableMappings: {},
                    enableRetries: false,
                    noOfRetries: 3,
                    waitTimeBetweenRetries: 5,
                    waitTimeUnit: 'minutes',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            callFlowNode: {
                type: 'callFlowNode',
                data: {
                    id: newId,
                    label: 'Call xflow',
                    selectedFlowId: '',
                    selectedFlowName: '',
                    flowStatus: '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            callWhatsappFlowNode: {
                type: 'callWhatsappFlowNode',
                data: {
                    id: newId,
                    label: 'WhatsApp Flow',
                    flowId: '',
                    bodyText: '',
                    kvPairs: [],
                    timeoutEnabled: false,
                    timeoutWaitTime: 30,
                    timeoutWaitUnit: 'minutes',
                    timeoutWaitReference: 'flow_trigger',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            end: {
                type: 'endNode',
                data: { id: newId, label: 'End', onDelete: handleDelete, },
            },
            action: {
                type: 'actionNode',
                data: {
                    id: newId,
                    label: 'Send Message',
                    channel: '',
                    template: '',
                    templateId: '',
                    variableMappings: {},
                    messageType: 'template', // Add this
                    freeformMessage: '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            delay: {
                type: subtype === 'dynamic' ? 'dynamicDelayNode' : 'delayNode',
                data: {
                    id: newId,
                    label: subtype === 'dynamic' ? 'Wait Time (Dynamic)' : 'Wait for Time',
                    duration: '',
                    unit: '',
                    delaySubtype: subtype || 'time',
                    dynamicWaitSource: 'event',
                    dynamicEventFieldType: 'resume_at',
                    dynamicEventFieldPath: 'event.delivery.resume_at',
                    fallbackDuration: '1',
                    fallbackUnit: 'days',
                    maxWaitDuration: '30',
                    maxWaitUnit: 'days',
                    dynamicApiNode: '',
                    dynamicApiField: 'resume_at',
                    dynamicExpression: '',

                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            condition: {
                type: 'conditionNode',
                data: {
                    id: newId,
                    label: 'Event Check',
                    eventSource: '',
                    conditionLogic: 'occurred',
                    timeWindowValue: '1',
                    timeWindowUnit: 'days',
                    yesLabel: 'Yes',
                    noLabel: 'No',
                    fallbackEnabled: false,
                    enableAttributeMatching: false,
                    matchAttribute: '',
                    referenceType: 'static',
                    previousEventRef: '',
                    messageVariable: '',
                    staticValue: '',
                    comparisonType: 'equals',
                    attributeFallbackEnabled: false,
                    countOperator: '',
                    countValue: 0,

                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            api: {
                type: 'apiNode',
                data: {
                    id: newId,
                    label: 'API Call',
                    apiMethod: 'GET',
                    apiEndpoint: '',
                    authType: 'none',
                    authHeaderKey: 'X-API-Key',
                    apiKey: '',
                    bearerToken: '',
                    basicUsername: '',
                    basicPassword: '',
                    executionMode: 'sync',
                    timeout: 30,
                    responseKey: 'api_response',
                    queryParams: [],
                    headers: [{ key: '', value: '' }],
                    bodyType: 'json',
                    apiPayload: '',
                    responseMapping: [{ responseField: '', saveAs: '' }],
                    handle4xx: false,
                    handle5xx: false,
                    retryEnabled: false,
                    retryAttempts: 3,
                    retryStrategy: 'exponential',
                    responseNamespace: 'api_response',
                    contractFields: [],
                    sampleJson: '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            // NEW: A/B Test Node
            abTest: {
                type: 'abTestNode',
                data: {
                    id: newId,
                    label: '',
                    numBranches: 2,
                    branches: { A: 50, B: 50 },
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            // NEW: Message Read Node
            messageRead: {
                type: 'messageReadNode',
                data: {
                    id: newId,
                    label: 'Message Delivery Status',
                    trackingMode: 'read',
                    waitTime: 24,
                    waitUnit: 'hours',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            // NEW: Update Profile Node
            updateProfile: {
                type: 'updateProfileNode',
                data: {
                    id: newId,
                    label: 'Update Customer Profile',
                    tagName: '',
                    // tagValue: '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            multiStep: {
                type: 'multiStepNode',
                data: {
                    id: newId,
                    label: 'Multi Step',
                    stepCount: 3,
                    stepNames: ['Step 1', 'Step 2', 'Step 3'],
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            buttonClicked: {
                type: 'buttonClickedNode',
                data: {
                    id: newId,
                    label: '',
                    buttons: [
                        { name: '', id: 'button_1' },
                        { name: '', id: 'button_2' },
                        { name: '', id: 'button_3' }
                    ],
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
            merge: {
                type: 'mergeNode',
                data: {
                    id: newId,
                    label: 'Merge Branches',
                    inputCount: 3,
                    mergeBehavior: 'any',
                    continueOnce: true,
                    timeoutEnabled: false,
                    timeoutValue: 30,
                    timeoutUnit: 'minutes',
                    continueOnTimeout: false,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            },
        };

        const newNode = nodeDefaults[type];
        if (newNode) {
            setNodes((nds) => [...nds, { id: newId, position, ...newNode }]);
            toast({
                title: 'Node added',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleEdit, handleDelete, handleDuplicate, toast, lastNodePosition]);

    // ==================== REACT FLOW HANDLERS ====================

    const onNodesChange = useCallback(
        (changes) => {
            setNodes((nds) => {
                const updatedNodes = applyNodeChanges(changes, nds);

                // Track last moved node position
                changes.forEach(change => {
                    if (change.type === 'position' && change.position) {
                        setLastNodePosition(change.position);
                    }
                });

                return updatedNodes;
            });
        },
        []
    );

    useEffect(() => {
        if (nodes.length > 0) {
            const lastNode = nodes[nodes.length - 1];
            if (lastNode.position) {
                setLastNodePosition(lastNode.position);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodes.length]);
    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );

    const onConnect = useCallback((params) => {
        // A hand-drawn edge gets the same colour it would have had if the backend had
        // sent it, so drawing a "Yes" branch by hand looks like a loaded one.
        const newEdge = {
            ...params,
            ...EDGE_DEFAULTS,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: edgeStyle(params.sourceHandle, nodesRef.current.find((n) => n.id === params.source)?.type),
        };
        setEdges((eds) => addEdge(newEdge, eds));
    }, []);

    const handleNodeClick = useCallback((event, node) => {
        setSelectedNode(node);
        setIsRightPanelOpen(true);
    }, []);

    const handlePaneClick = useCallback(() => {
        setSelectedNode(null);
        setIsRightPanelOpen(false);
    }, []);

    const updateNodeData = useCallback(() => {
        if (!selectedNodeRef.current) return;
        if (selectedNodeRef.current.type === 'abTestNode') {
            const total = Object.values(nodeConfig.branches || {}).reduce((sum, val) => sum + (typeof val === 'number' ? val : 0), 0);
            if (total !== 100) {
                toast({
                    title: 'Validation Error',
                    description: 'Traffic split must equal 100%. Please adjust the percentages.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
        }

        const newLabel = nodeConfig.label?.trim();
        if (newLabel) {
            const currentNodeId = selectedNodeRef.current.id;
            const duplicate = nodesRef.current.find(
                (n) => n.id !== currentNodeId && n.type !== 'endNode' && n.data?.label?.trim() === newLabel
            );
            if (duplicate) {
                toast({
                    title: 'Duplicate Step Name',
                    description: `"${newLabel}" is already used by "${duplicate.data?.label}" node. Please use a unique step name.`,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
        }

       
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === selectedNodeRef.current.id) {
                    const { onEdit, onDelete, onDuplicate } = node.data;

                    let updatedData = { ...nodeConfig };

                    if (node.type === 'apiNode') {
                        updatedData = {
                            label: nodeConfig.label,
                            apiMethod: nodeConfig.apiMethod,
                            apiEndpoint: nodeConfig.apiEndpoint,
                            authType: nodeConfig.authType,
                            authHeaderKey: nodeConfig.authHeaderKey,
                            apiKey: nodeConfig.apiKey,
                            bearerToken: nodeConfig.bearerToken,
                            basicUsername: nodeConfig.basicUsername,
                            basicPassword: nodeConfig.basicPassword,
                            executionMode: nodeConfig.executionMode,
                            timeout: nodeConfig.timeout,
                            responseKey: nodeConfig.responseKey,
                            queryParams: nodeConfig.queryParams || [],
                            headers: nodeConfig.headers || [{ key: '', value: '' }],
                            bodyType: nodeConfig.bodyType,
                            apiPayload: nodeConfig.apiPayload,
                            responseMapping: nodeConfig.responseMapping || [{ responseField: '', saveAs: '' }],
                            handle4xx: nodeConfig.handle4xx,
                            handle5xx: nodeConfig.handle5xx,
                            retryEnabled: nodeConfig.retryEnabled,
                            retryAttempts: nodeConfig.retryAttempts,
                            retryStrategy: nodeConfig.retryStrategy,
                            responseNamespace: nodeConfig.responseNamespace,
                            contractFields: nodeConfig.contractFields,
                            sampleJson: nodeConfig.sampleJson,
                            // Legacy compatibility
                            method: nodeConfig.apiMethod,
                            endpoint: nodeConfig.apiEndpoint,
                            payload: nodeConfig.apiPayload,
                            customHeaders: JSON.stringify(
                                (nodeConfig.headers || [])
                                    .filter(h => h.key)
                                    .reduce((acc, h) => ({ ...acc, [h.key]: h.value }), {}),
                                null, 2
                            ),
                        };
                    }

                    return {
                        ...node,
                        data: {
                            ...node.data,
                            ...updatedData,
                            onEdit,
                            onDelete,
                            onDuplicate,
                        },
                    };
                }
                return node;
            })
        );

        toast({
            title: 'Node Updated',
            description: 'Node Updated Successfully',
            status: 'success',
            duration: 3000,
            isClosable: true,
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeConfig]);

    // ==================== SAVE & TEMPLATE HANDLERS ====================

    const validateFlow = (requireEndNode = false) => {
        if (!campaignName.trim()) {
            toast({
                title: 'Validation Error',
                description: 'Please enter a campaign name',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        if (nodes.length === 0) {
            toast({
                title: 'Validation Error',
                description: 'Please add at least one node to the flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        const startNode = nodes.find(n => n.type === 'startNode');
        if (!startNode) {
            toast({
                title: 'Validation Error',
                description: 'Your flow must have a start node',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        if (requireEndNode) {
            const endNode = nodes.find(n => n.type === 'endNode');
            if (!endNode) {
                toast({
                    title: 'Validation Error',
                    description: 'Your flow must have an end node',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }

            const disconnectedNodes = nodes.filter(n => {
                if (n.type === 'endNode') return false;
                const hasOutgoingEdge = edges.some(e => e.source === n.id);
                return !hasOutgoingEdge;
            });

            if (disconnectedNodes.length > 0) {
                toast({
                    title: 'Validation Error',
                    description: `All nodes must be connected. Node(s) "${disconnectedNodes.map(n => n.data?.label || n.type).join(', ')}" have no outgoing connection.`,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }

            // Check that all required handles on multi-output nodes are connected
            const handleErrors = [];
            for (const node of nodes) {
                if (node.type === 'endNode') continue;
                const nodeEdges = edges.filter(e => e.source === node.id);
                const label = node.data?.label || node.type;

                if (['conditionNode', 'enhancedConditionNode', 'messageReadNode'].includes(node.type)) {
                    if (!nodeEdges.some(e => e.sourceHandle === 'yes'))
                        handleErrors.push(`"${label}" — "Yes" path is not connected`);
                    if (!nodeEdges.some(e => e.sourceHandle === 'no'))
                        handleErrors.push(`"${label}" — "No" path is not connected`);
                }

                if (node.type === 'customerProfileNode') {
                    if (!nodeEdges.some(e => e.sourceHandle === 'true'))
                        handleErrors.push(`"${label}" — "Match Found" path is not connected`);
                    if (!nodeEdges.some(e => e.sourceHandle === 'false'))
                        handleErrors.push(`"${label}" — "No Match" path is not connected`);
                }

                if (node.type === 'checkChannelConsentNode') {
                    if (!nodeEdges.some(e => e.sourceHandle === 'enabled'))
                        handleErrors.push(`"${label}" — "${node.data?.enabledLabel || 'Enabled'}" path is not connected`);
                    if (!nodeEdges.some(e => e.sourceHandle === 'disabled'))
                        handleErrors.push(`"${label}" — "${node.data?.disabledLabel || 'Disabled'}" path is not connected`);
                }

                if (node.type === 'abTestNode') {
                    const numBranches = node.data?.numBranches || 2;
                    const branchLabels = Array.from({ length: numBranches }, (_, i) =>
                        String.fromCharCode(65 + i).toLowerCase()
                    );
                    branchLabels.forEach(branch => {
                        if (!nodeEdges.some(e => e.sourceHandle === branch))
                            handleErrors.push(`"${label}" — Branch "${branch.toUpperCase()}" is not connected`);
                    });
                }

                if (node.type === 'apiNode' && node.data?.executionMode !== 'async') {
                    if (!nodeEdges.some(e => e.sourceHandle === 'success'))
                        handleErrors.push(`"${label}" — "Success" path is not connected`);
                    if (!nodeEdges.some(e => e.sourceHandle === 'error'))
                        handleErrors.push(`"${label}" — "Error" path is not connected`);
                }

                if (node.type === 'smartConditionNode') {
                    const groups = node.data?.groups || [];
                    groups.forEach(group => {
                        if (!nodeEdges.some(e => e.sourceHandle === `group-${group.id}`))
                            handleErrors.push(`"${label}" — Group "${group.label || group.id}" path is not connected`);
                    });
                    if (!nodeEdges.some(e => e.sourceHandle === 'default'))
                        handleErrors.push(`"${label}" — "No Match" default path is not connected`);
                }
            }

            if (handleErrors.length > 0) {
                toast({
                    title: 'Validation Error',
                    description: handleErrors[0] + (handleErrors.length > 1 ? ` (and ${handleErrors.length - 1} more)` : ''),
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                return false;
            }
        }

        // Check for duplicate step names across all nodes (excluding end nodes)
        const labelCounts = {};
        nodes.forEach((n) => {
            if (n.type === 'endNode') return;
            const label = n.data?.label?.trim();
            if (label) {
                labelCounts[label] = (labelCounts[label] || 0) + 1;
            }
        });
        const duplicateLabels = Object.entries(labelCounts)
            .filter(([, count]) => count > 1)
            .map(([label]) => label);
        if (duplicateLabels.length > 0) {
            toast({
                title: 'Duplicate Step Names',
                description: `The following step names are used more than once: "${duplicateLabels.join('", "')}". Each step must have a unique name.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return false;
        }

        return true;
    };

    const saveFlow = async (shouldNavigate = true) => {
        setIsSaving(true);
        try {
            const payload = transformToBackendPayload({
                name: campaignName,
                nodes,
                edges,
                status: flowStatus,
            });

            if (savedFlowIdRef.current) {
                await updateFlow(savedFlowIdRef.current, payload);
                toast({
                    title: 'Flow updated successfully',
                    description: `"${campaignName}" has been updated`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                const response = await createFlow(payload);
                const newId = response?.flow_id || response?._id || response?.data?._id;
                if (newId) {
                    savedFlowIdRef.current = newId;
                    navigate(`/marketing/flow/${newId}`, { replace: true });
                }
                toast({
                    title: 'Flow created successfully',
                    description: `"${campaignName}" has been created`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }

            if (shouldNavigate) {
                setTimeout(() => { navigate('/marketing/flow'); }, 1500);
            }
        } catch (error) {
            console.error('Error saving flow:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save flow';
            toast({
                title: 'Error saving flow',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        if (!validateFlow(true)) return;
        await saveFlow(true);
    };

    const handleSaveAndContinue = async () => {
        if (!validateFlow(false)) return;
        await saveFlow(false);
    };


    const handleBack = () => {
        navigate('/marketing/flow');
    };

    const handleLoadTemplate = useCallback((templateId) => {
        const template = loadTemplateFlows(templateId, handleEdit, handleDelete, handleDuplicate);
        if (template) {
            setNodes(template.nodes);
            setEdges(withEdgeStyles(template.edges, template.nodes));
            setCampaignName(template.name);
            setExamplesDialogOpen(false);
        }
    }, [handleEdit, handleDelete, handleDuplicate]);

    // ==================== RENDER ====================

    if (isLoading) {
        return (
            <Flex h="100vh" justify="center" align="center" bg="gray.50">
                <Text>Loading flow...</Text>
            </Flex>
        );
    }

    return (
        <Flex h="100vh" direction="column" bg="gray.50">
            {/* Header Component */}
            <FlowHeader
                campaignName={campaignName}
                setCampaignName={setCampaignName}
                flowStatus={flowStatus}
                setFlowStatus={setFlowStatus}
                nodes={nodes}
                onBack={handleBack}
                onSave={handleSave}
                onSaveAndContinue={handleSaveAndContinue}
                onLoadExamples={() => setExamplesDialogOpen(true)}
                isSaving={isSaving}
                isEdit={isEdit}
            />

            {/* Toolbar Component */}
            <FlowToolbar
                showGrid={showGrid}
                setShowGrid={setShowGrid}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onFitView={handleFitView}
                currentZoom={currentZoom}

            />

            <Flex flex={1} overflow="hidden">
                {/* Left Sidebar - Component Library */}
                <ComponentLibrary
                    onAddNode={addNode}
                    node={selectedNode}
                    isEdit={isEdit}
                    isMinimized={isLeftPanelMinimized}
                    onToggle={() => setIsLeftPanelMinimized(v => !v)}
                />

                {/* Center - React Flow Canvas */}
                <Flex flex={1} position="relative">
                    <ReactFlow
                        // scopes flowHandles.css to this canvas — MetricTree renders its
                        // own ReactFlow and must keep its handle sizing
                        className="xflow-canvas"
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        // onNodeClick={(isEdit && id) ? null : handleNodeClick}
                        onPaneClick={handlePaneClick}
                        nodeTypes={nodeTypes}
                        minZoom={0.1}
                        maxZoom={2}
                        onInit={(instance) => {
                            reactFlowInstance.current = instance;
                            setCurrentZoom(Math.round(instance.getZoom() * 100));
                        }}
                        onMove={(event, viewport) => {
                            setCurrentZoom(Math.round(viewport.zoom * 100));
                        }}
                    >
                        <Background variant={showGrid ? BackgroundVariant.Dots : BackgroundVariant.Lines} />
                        <Controls />
                    </ReactFlow>

                    {/* Right panel open toggle — shows when panel is closed */}
                    {!isRightPanelOpen && (
                        <Box
                            position="absolute"
                            right={0}
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex={10}
                        >
                            <Tooltip label="Open properties panel" placement="left">
                                <IconButton
                                    size="sm"
                                    icon={<FiChevronLeft />}
                                    onClick={() => setIsRightPanelOpen(true)}
                                    aria-label="Open properties panel"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRightRadius={0}
                                    borderRight="none"
                                    boxShadow="md"
                                    _hover={{ bg: 'gray.50' }}
                                />
                            </Tooltip>
                        </Box>
                    )}
                </Flex>

                {/* Right Sidebar - Property Panel (only when open) */}
                {isRightPanelOpen && (
                    <PropertyPanel
                        selectedNode={selectedNode}
                        nodeConfig={nodeConfig}
                        setNodeConfig={setNodeConfig}
                        updateNodeData={updateNodeData}
                        setSelectedNode={setSelectedNode}
                        edges={edges}
                        nodes={nodes}
                        onClose={() => {
                            setIsRightPanelOpen(false);
                            setSelectedNode(null);
                        }}
                    />
                )}
            </Flex>

            {/* Templates Dialog Modal */}
            <TemplatesDialog
                isOpen={examplesDialogOpen}
                onClose={() => setExamplesDialogOpen(false)}
                onLoadTemplate={handleLoadTemplate}
            />
        </Flex>
    );
}